'use strict';

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { isNumber } from 'lodash';
import { FormattedMessage, injectIntl } from 'react-intl';
import { removeToast } from '../data/toasts/actions';

let globalDismissDelay = 5000;

/**
 * Dismiss function can be self contained, or, when containing a callback,
 * invoked later.
 *
 * Func is checked for arguments, and if func expects callback,
 * the callback is injected into the function.
 * If func has no callback, does no change.
 *
 * The passed callback (dispatchDismiss) should be called with the
 * identifier (notificationKey) of the notification to be dismissed. e.g.
 *
 * (callback) => callback("NOTIFICATION_KEY")
 *
 * @param   {function} func            Dismiss function.
 * @param   {function} dispatchDismiss Callback function
 * @returns {function}               to be called for dismissing.
 * */
const getDismissFunction = (func, dispatchDismiss) => {
  if (func && func.length === 1) {
    return () => func(dispatchDismiss);
  } else {
    return func;
  }
};

/**
 * Component for displaying alerts.
 * @param {string}   type                Type of alert: [error|info|hpa]. These basically map to bootstrap's
 *                                       alert classes but have additional effects, e.g. type defines a
 *                                       default fallback message for error.
 * @param {array}    children            possible child components rendered inside alert.
 * @param {shape:{timeout:boolean, func:function}   dismiss Set timeout to true for automatic timeout.
 *                                                          Set func to a function to dismiss the alert.
 *                                                          If no func is supplied, there will be no button (x)
 *                                                          for dismissing the alert.
 * @param {string}   message             message shown in alert
 * @param {function} dispatchDismiss     Function injected by redux connect.
 * @param {boolean}  isHtml              whether message should be html. Changes underlying FormattedMessage component.
 * @param {string} linkText              Text for the link
 * @param {string} linkHref              href for the link
 *
 */
const AlertInternal = ({
  type = 'error',
  children,
  dismiss = {},
  message,
  isHtml = false,
  linkText,
  linkHref,
  linkTarget,
  dispatchDismiss,
  intl,
  className,
  style,
  ...rest
}) => {
  // check message exists, use fallback?
  if (message) {
    let messageExists = !!intl.messages[message];
    if (!messageExists) {
      if (type === 'error') {
        //put default fallback
        message = 'default.error.message';
      } else {
        // no default message on other types yet
        message = '';
      }
    }
  }

  let dismissFunction = getDismissFunction(dismiss.func, dispatchDismiss);

  if (dismiss.timeout) {
    if (isNumber(dismiss.timeout)) {
      setTimeout(dismissFunction, dismiss.timeout);
    } else {
      setTimeout(dismissFunction, globalDismissDelay);
    }
  }

  return (
    <article
      role="alert"
      className={classnames(className, `alert alert-${type}`)}
      style={style}
    >
      {dismiss.func && (
        <button
          aria-label={intl.messages['dismiss']}
          className="alert-dismiss icon-remove pull-right"
          onClick={dismissFunction}
        />
      )}
      {message &&
        (isHtml ? (
          <FormattedMessage
            id={message}
            values={{
              strong: (chunks) => <strong>{chunks}</strong>,
              b: (chunks) => <b>{chunks}</b>,
              link: (
                <a target={linkTarget} href={linkHref}>
                  {linkText}
                </a>
              ),
            }}
          />
        ) : (
          <FormattedMessage id={message} />
        ))}
      {children}
    </article>
  );
};

AlertInternal.propTypes = {
  type: PropTypes.string,
  children: PropTypes.object,
  dismiss: PropTypes.shape({
    timeout: PropTypes.bool,
    func: PropTypes.func,
  }),
  message: PropTypes.string,
  isHtml: PropTypes.bool,
  dispatchDismiss: PropTypes.func,
  intl: PropTypes.object,
};

const mapStateToProps = () => {
  return {
    FormattedMessage,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    dispatchDismiss: (identifier) => dispatch(removeToast(identifier)),
  };
};

const Alert = connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(AlertInternal));

export default Alert;
